/**
 * Which first-party micro-frontends the platform gives the caller.
 *
 * A first-party MFE is a plugin in the registry: a kill, a distribution policy,
 * an organisation's entitlement and a workspace's enablement decide whether it
 * is the caller's. The host hands the registry's answer here - from its resolve
 * on boot, then from every `resolved` event on the live stream - and a route or
 * region that names the plugin follows it: excluded, it shows its unavailable
 * card with the reason; resolved again, it loads the remote in, where the
 * reader is.
 *
 * On the server the render's `services.plugins` answers instead, so two
 * callers rendered at once each get their own.
 *
 * Only an explicit exclusion takes an MFE away. A host that has not heard
 * from the registry (none configured, or not yet answered), or a plugin the
 * registry did not consider for this caller, loads as before: the registry
 * withholds MFEs, and its absence must not take the platform down with it.
 */
import { Signal } from '@crown/signal';
import { hasGlobal, isList, isString } from '../ir/guards';
import { isJsonObject, parseJson } from '../ir/json';
import type { JsonValue } from '../ir/types';

export type PluginState = 'resolved' | 'excluded' | 'unknown';

export interface PluginSet {
  /** Plugin keys the caller gets. */
  readonly resolved: readonly string[];
  /** Plugin keys withheld from the caller, with the registry's reason (`killed`, `not_enabled`, …). */
  readonly excluded: Readonly<Record<string, string>>;
  /** Keys killed for a security reason: a page that has loaded one reloads to be rid of its code. */
  readonly security?: readonly string[];
}

/** The id of the script that carries a server render's plugin set to the browser. */
export const PLUGINS_SCRIPT_ID = 'c-plugins';

/** Where the active server render's plugin set is found; set by the server context module. */
export const SERVER_PLUGINS = Symbol.for('crown.plugins.server');
const STATE = Symbol.for('crown.plugins.state');
const LOADED = Symbol.for('crown.plugins.loaded');
const RELOAD = Symbol.for('crown.plugins.reload');

const holder: typeof globalThis & {
  [SERVER_PLUGINS]?: () => PluginSet | undefined;
  [STATE]?: Signal.State<PluginSet | null>;
  [LOADED]?: Set<string>;
  [RELOAD]?: () => void;
} = globalThis;

/** A field of a JSON object, or nothing when the value is not one. */
function field(
  value: JsonValue | undefined,
  name: string,
): JsonValue | undefined {
  return value !== undefined && isJsonObject(value) ? value[name] : undefined;
}

/** The strings in a JSON list; anything else in it, or a value that is not a list, gives none. */
function strings(list: JsonValue | undefined): string[] {
  return isList(list) ? list.filter(isString) : [];
}

/** The set a server render wrote into the page, when there is one. */
function fromPage(): PluginSet | null {
  if (!hasGlobal('document')) return null;
  const script = document.getElementById(PLUGINS_SCRIPT_ID);
  if (!script?.textContent) return null;
  const parsed = parseJson(script.textContent);
  if (!parsed.ok) return null;
  const excluded: Record<string, string> = {};
  const given = field(parsed.value, 'excluded');
  if (given !== undefined && isJsonObject(given)) {
    for (const [key, reason] of Object.entries(given))
      if (isString(reason)) excluded[key] = reason;
  }
  return {
    resolved: strings(field(parsed.value, 'resolved')),
    excluded,
    security: strings(field(parsed.value, 'security')),
  };
}

function browserSet(): Signal.State<PluginSet | null> {
  // The page starts from what the server rendered with, so an MFE the server
  // withheld is not loaded while the live stream connects.
  holder[STATE] ??= new Signal.State<PluginSet | null>(fromPage());
  return holder[STATE];
}

/** Records that this page has loaded a plugin's code; a security kill of it then reloads the page. */
export function markPluginLoaded(pluginKey: string): void {
  (holder[LOADED] ??= new Set()).add(pluginKey);
  // Code that finished loading after the platform killed it for security.
  if (holder[STATE]?.get()?.security?.includes(pluginKey)) reload();
}

function reload(): void {
  (holder[RELOAD] ?? (() => globalThis.location?.reload()))();
}

/** How the page reloads; the browser's own unless a test says otherwise. */
export function setPluginReload(next: () => void): void {
  holder[RELOAD] = next;
}

/** The plugin set as the page carries it to the browser, for a server render to write. */
export function pluginsScript(
  set: PluginSet,
  inlineJson: (value: JsonValue) => string,
): string {
  return `<script type="application/json" id="${PLUGINS_SCRIPT_ID}">${inlineJson({ resolved: set.resolved, excluded: set.excluded, security: set.security ?? [] })}</script>`;
}

function current(): PluginSet | null {
  const server = holder[SERVER_PLUGINS]?.();
  if (server) return server;
  return browserSet().get();
}

/** Replaces the browser's plugin set with the registry's latest answer. */
export function providePluginSet(set: PluginSet): void {
  browserSet().set({
    resolved: [...set.resolved],
    excluded: { ...set.excluded },
    security: [...(set.security ?? [])],
  });
  // Unmounting cannot take back code the page has run: a security kill of
  // something it loaded reloads it, as a security kill of any plugin does.
  const loaded = holder[LOADED];
  if (loaded && (set.security ?? []).some((key) => loaded.has(key))) reload();
}

/**
 * The registry's resolve answer (from `/api/plugins/resolve/context` or a
 * `resolved` event) as a plugin set: first-party and registry plugins resolved,
 * everything excluded with its reason.
 */
export function pluginSetFrom(
  resolution: JsonValue | undefined,
): PluginSet | null {
  if (resolution === undefined || !isJsonObject(resolution)) return null;
  const keys = (list: JsonValue | undefined): string[] =>
    isList(list)
      ? list.flatMap((entry) => {
          const key = field(entry, 'pluginKey');
          return isString(key) ? [key] : [];
        })
      : [];
  const excluded: Record<string, string> = {};
  const security: string[] = [];
  const given = field(resolution, 'excluded');
  if (isList(given)) {
    for (const entry of given) {
      const key = field(entry, 'pluginKey');
      if (!isString(key)) continue;
      const reason = field(entry, 'reason');
      excluded[key] = isString(reason) ? reason : 'excluded';
      if (field(entry, 'severity') === 'security') security.push(key);
    }
  }
  return {
    resolved: [
      ...keys(field(resolution, 'firstParty')),
      ...keys(field(resolution, 'plugins')),
    ],
    excluded,
    security,
  };
}

/** Whether the caller gets `pluginKey`; a tracked read in the browser. */
export function pluginState(pluginKey: string): PluginState {
  const set = current();
  if (!set) return 'unknown';
  if (set.resolved.includes(pluginKey)) return 'resolved';
  if (pluginKey in set.excluded) return 'excluded';
  // Neither: the registry did not consider it for this caller (no active workspace, or a
  // plugin it does not know). Only an explicit exclusion takes an MFE away.
  return 'unknown';
}

/** The registry's reason for withholding `pluginKey`, when it is withheld. */
export function pluginExclusion(pluginKey: string): string | null {
  const set = current();
  if (!set) return null;
  return set.excluded[pluginKey] ?? null;
}

/** Forgets the browser's plugin set; for tests. */
export function resetPluginSetForTests(): void {
  delete holder[STATE];
  delete holder[LOADED];
  delete holder[RELOAD];
}
